import { LEAN_LIBRARY } from '../data/leanLibrary';
import type { LstTemplateInput } from './lstService';
import type { LstCargo } from '../types';
import type {
  LeanCategoria,
  LeanFrequenciaLabel,
  LeanLibraryImportPayload,
  LeanLibraryItem,
  LeanNivel,
} from '../types/leanLibrary';
import {
  isLeanCategoria,
  isLeanFrequenciaLabel,
  isLeanNivel,
  leanItemToTemplatePayload,
  leanNivelToLstCargo,
  resolveLeanNivelForUser,
} from '../types/leanLibrary';

export interface LeanLibraryFilters {
  nivel?: LeanNivel;
  frequencia?: LeanFrequenciaLabel | '';
  categoria?: LeanCategoria | '';
  search?: string;
}

export interface LeanLibraryStats {
  total: number;
  byNivel: Record<LeanNivel, number>;
  byCategoria: Record<LeanCategoria, number>;
}

function normalizeSearchQuery(search?: string): string {
  return search?.trim().toLowerCase() ?? '';
}

function itemMatchesSearch(item: LeanLibraryItem, query: string): boolean {
  if (!query) return true;
  const haystack = [
    item.titulo,
    item.descricao,
    item.objetivo,
    item.evidenciaEsperada,
    item.categoria,
    item.frequenciaRecomendada,
    ...item.tags,
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

export function filterLeanLibrary(filters: LeanLibraryFilters): LeanLibraryItem[] {
  const query = normalizeSearchQuery(filters.search);

  return LEAN_LIBRARY.filter((item) => {
    if (filters.nivel && !item.nivelHierarquico.includes(filters.nivel)) return false;
    if (filters.frequencia && item.frequenciaRecomendada !== filters.frequencia) return false;
    if (filters.categoria && item.categoria !== filters.categoria) return false;
    return itemMatchesSearch(item, query);
  });
}

export function getLibraryForUser(
  lstCargo: LstCargo,
  roleName?: string | null,
  extraFilters?: Omit<LeanLibraryFilters, 'nivel'>,
): LeanLibraryItem[] {
  const nivel = resolveLeanNivelForUser(lstCargo, roleName);
  return filterLeanLibrary({ ...extraFilters, nivel });
}

export function getLeanLibraryItemById(id: string): LeanLibraryItem | undefined {
  return LEAN_LIBRARY.find((item) => item.id === id);
}

export function countLibraryByNivel(): Record<LeanNivel, number> {
  const counts = {} as Record<LeanNivel, number>;
  for (const item of LEAN_LIBRARY) {
    for (const nivel of item.nivelHierarquico) {
      counts[nivel] = (counts[nivel] ?? 0) + 1;
    }
  }
  return counts;
}

export function computeLeanLibraryStats(): LeanLibraryStats {
  const byNivel = countLibraryByNivel();
  const byCategoria = {} as Record<LeanCategoria, number>;

  for (const item of LEAN_LIBRARY) {
    byCategoria[item.categoria] = (byCategoria[item.categoria] ?? 0) + 1;
  }

  return {
    total: LEAN_LIBRARY.length,
    byNivel,
    byCategoria,
  };
}

/** Converte item da biblioteca estática em payload pronto para lst_templates. */
export function mapLibraryItemToTemplateInput(
  item: LeanLibraryItem,
  organizationId: string,
  targetRole: LstCargo,
  createdBy?: string | null,
  scheduledTime = '08:00',
): LstTemplateInput {
  const payload: LeanLibraryImportPayload = leanItemToTemplatePayload(item);
  const primaryNivel = item.nivelHierarquico[0];
  const resolvedRole = primaryNivel ? leanNivelToLstCargo(primaryNivel) : targetRole;

  return {
    organization_id: organizationId,
    target_role: resolvedRole,
    title: payload.title,
    description: payload.description ?? null,
    frequency: payload.frequency,
    period: 'inicio',
    category: payload.category,
    is_critical: payload.is_critical,
    duration_minutes: payload.duration_minutes,
    activity_kind: payload.activity_kind,
    swi_mode: 'text',
    swi_text: payload.swi_text,
    swi_doc_url: null,
    swi_checklist: payload.swi_checklist,
    scheduled_time: scheduledTime,
    alert_lead_minutes: 10,
    alert_channel: 'andon',
    linked_indicator_id: null,
    linked_indicator_name: payload.linked_indicator_name,
    linked_indicator_target: null,
    created_by: createdBy ?? null,
  };
}

export function parseLeanLibraryFilters(raw: {
  nivel?: string;
  frequencia?: string;
  categoria?: string;
  search?: string;
}): LeanLibraryFilters {
  return {
    search: raw.search,
    nivel: raw.nivel && isLeanNivel(raw.nivel) ? raw.nivel : undefined,
    frequencia:
      raw.frequencia && isLeanFrequenciaLabel(raw.frequencia) ? raw.frequencia : '',
    categoria: raw.categoria && isLeanCategoria(raw.categoria) ? raw.categoria : '',
  };
}
